import { jsx as _jsx } from "react/jsx-runtime";
import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import styles from '@/styles/widget.css?inline';
import { applyTheme } from '@/core/theme';
import { resolveConfig } from '@/core/config';
import { ChatEngine } from '@/state/chatStore';
import { ChatProvider } from '@/ui/ChatProvider';
import { ChatWidget } from '@/ui/ChatWidget';
import { StandaloneChat } from '@/ui/StandaloneChat';
/**
 * Creates a widget instance. Called by the embed script, and reusable from
 * an app that wants a second isolated widget on the same page.
 */
export function mount(options = {}) {
    const { target, shadow = true, ...configInput } = options;
    const container = resolveTarget(target);
    const host = document.createElement('div');
    host.className = 'ac-host';
    container.appendChild(host);
    let mountPoint;
    if (shadow) {
        const root = host.attachShadow({ mode: 'open' });
        const sheet = document.createElement('style');
        sheet.textContent = styles;
        root.appendChild(sheet);
        mountPoint = document.createElement('div');
        root.appendChild(mountPoint);
        // Tokens live on the shadow host so page CSS can override them if wanted.
        applyTheme(host, resolveConfig(configInput).theme);
    }
    else {
        injectGlobalStyles();
        mountPoint = host;
    }
    mountPoint.className = 'ac-root';
    const config = resolveConfig(configInput);
    applyTheme(mountPoint, config.theme);
    const engine = new ChatEngine(config);
    const root = createRoot(mountPoint);
    root.render(_jsx(StrictMode, { children: _jsx(ChatProvider, { engine: engine, children: config.presentation === 'fullscreen' ? _jsx(StandaloneChat, {}) : _jsx(ChatWidget, {}) }) }));
    let context = { ...config.context };
    return {
        engine,
        logger: engine.logger,
        open(options) {
            engine.actions.open();
            if (options?.view)
                engine.actions.showView(options.view);
        },
        close: () => engine.actions.close(),
        toggle: () => engine.actions.toggle(),
        sendMessage: (text) => engine.actions.send(text),
        reset: () => engine.actions.reset(),
        logout() {
            config.transport.logout?.();
            return engine.actions.reset();
        },
        setContext(next) {
            context = { ...context, ...next };
            Object.assign(engine.getConfig(), { context });
        },
        updateTheme(theme) {
            if (!theme)
                return;
            Object.assign(config.theme, theme);
            applyTheme(mountPoint, config.theme);
            if (shadow)
                applyTheme(host, config.theme);
        },
        on: (event, listener) => engine.events.on(event, listener),
        destroy() {
            engine.dispose();
            // Unmount out of band: React forbids sync unmount during a render pass.
            setTimeout(() => {
                root.unmount();
                host.remove();
            }, 0);
        },
    };
}
function resolveTarget(target) {
    if (!target)
        return document.body;
    if (typeof target === 'string') {
        const found = document.querySelector(target);
        if (!found)
            throw new Error(`[agentic-chat] target "${target}" not found`);
        return found;
    }
    return target;
}
let globalStylesInjected = false;
function injectGlobalStyles() {
    if (globalStylesInjected)
        return;
    const sheet = document.createElement('style');
    sheet.dataset.agenticChat = '';
    sheet.textContent = styles;
    document.head.appendChild(sheet);
    globalStylesInjected = true;
}
